import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { patientApi, type ImagingOrderRecord, type ImagingOrderDetail } from '../api';

/**
 * The patient's REAL imaging orders (`GET /imaging/orders`) — studies a doctor
 * or a hospital ordered, progressed by the imaging centre working them.
 *
 * Shared so that Care Services → Imaging and Imaging & Diagnostics show the
 * same thing. They used to be different surfaces: Care Services led with a
 * centre directory and a booking flow, and the orders a hospital had actually
 * placed lived on another page the patient had to find.
 */

type FilterTab = 'All' | 'Scheduled' | 'Report ready' | 'Cancelled';

const STATUS_COLORS: Record<string, string> = {
  ORDERED: T.info,
  SCHEDULED: T.teal,
  IN_PROGRESS: T.warning,
  REPORT_PENDING: T.warning,
  ACQUIRED: T.warning,
  REPORT_READY: T.success,
  COMPLETED: T.success,
  CANCELLED: T.danger,
};

function prettyStatus(status: string): string {
  return (status || '').replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase());
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** The study as a patient recognises it — "2D Echocardiography". */
function titleOf(o: ImagingOrderRecord): string {
  return o.testName || [o.modality, o.bodyPart].filter(Boolean).join(' · ') || 'Imaging study';
}

/** "ECHO · Heart" underneath it, and nothing when it repeats the title. */
function subtitleOf(o: ImagingOrderRecord): string {
  const parts = [o.modality, o.bodyPart].filter(Boolean).join(' · ');
  return parts === titleOf(o) ? '' : parts;
}

/** URGENT / STAT / EMERGENCY — the priorities worth a red chip. */
function isUrgent(o: ImagingOrderRecord): boolean {
  const p = (o.priority || '').toUpperCase();
  return p === 'URGENT' || p === 'STAT' || p === 'EMERGENCY';
}

/** A small pill, the same vocabulary the app's card uses. */
function Chip({ label, tone }: { label: string; tone?: 'danger' | 'success' | 'plain' }) {
  const color = tone === 'danger' ? T.danger : tone === 'success' ? T.success : T.textSec;
  const solid = tone === 'danger' || tone === 'success';
  return (
    <span style={{
      fontFamily: T.font, fontSize: 8.5, fontWeight: 700,
      color: solid ? color : T.textSec,
      background: solid ? `${color}14` : T.bg,
      border: `1px solid ${solid ? `${color}35` : T.border}`,
      borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap',
    }}>{label}</span>
  );
}

/** The scan glyph the app shows beside every order. */
function ScanIcon() {
  return (
    <div style={{
      width: 34, height: 34, borderRadius: 10, background: `${T.teal}12`,
      display: 'grid', placeItems: 'center', flexShrink: 0,
    }}>
      <svg width={17} height={17} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
        <path d="M7 13h2l2-4 2 8 2-4h2" />
      </svg>
    </div>
  );
}

/** "MRI — Brain" from one study a doctor recorded, skipping what they left blank. */
function studyLabel(s: Record<string, unknown>): string {
  const parts = [s.modality, s.bodyPart]
    .filter((v) => v != null && String(v).trim())
    .map(String);
  return parts.join(' — ') || 'Imaging study';
}

/** The slot the CENTRE booked, never a time this app picked. */
function slotLabel(d: ImagingOrderDetail): string {
  const date = d.schedule?.slotDate ?? null;
  const time = d.schedule?.slotTime ?? null;
  if (!date && !time) return '';
  return [date ? fmtDate(date) : null, time].filter(Boolean).join(', ');
}

export default function ImagingOrders({ compact = false }: { compact?: boolean }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<ImagingOrderRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('All');
  // One order opened at a time; the detail carries the radiologist's report.
  const [openId, setOpenId] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, ImagingOrderDetail>>({});
  const [detailError, setDetailError] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const rows = await patientApi.getImagingOrders();
      setOrders(Array.isArray(rows) ? rows : []);
    } catch (e: any) {
      setError(e?.body?.message || 'Your imaging orders could not be loaded. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const toggle = async (id: string) => {
    if (openId === id) { setOpenId(null); return; }
    setOpenId(id);
    if (details[id] || detailError[id]) return;
    try {
      const d = await patientApi.getImagingOrder(id);
      setDetails((prev) => ({ ...prev, [id]: d }));
    } catch (e: any) {
      setDetailError((prev) => ({ ...prev, [id]: e?.body?.message || 'This order could not be opened.' }));
    }
  };

  const filteredOrders = orders.filter(o => {
    const s = (o.status || '').toUpperCase();
    if (activeTab === 'All') return true;
    if (activeTab === 'Report ready') return o.reportAvailable || s === 'REPORT_READY' || s === 'COMPLETED';
    if (activeTab === 'Cancelled') return s.includes('CANCEL');
    // Scheduled = everything still on its way.
    return !o.reportAvailable && !s.includes('CANCEL') && s !== 'COMPLETED';
  });

  // On a shared page the tabs are noise until there is enough to sift.
  const showTabs = !compact && orders.length > 2;
  const tabs: FilterTab[] = ['All', 'Scheduled', 'Report ready', 'Cancelled'];
  const shown = compact ? filteredOrders.slice(0, 3) : filteredOrders;

  if (loading) {
    return (
      <div style={{
        background: T.white, border: `1px solid ${T.border}`, borderRadius: 16,
        padding: 28, textAlign: 'center', fontSize: 11, color: T.textSec, fontFamily: T.font,
      }}>
        Loading your imaging orders…
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: T.white, border: `1px solid ${T.danger}40`, borderRadius: 16,
        padding: 24, textAlign: 'center', fontFamily: T.font,
      }}>
        <div style={{ fontSize: 10.5, color: T.danger, fontWeight: 600 }}>{error}</div>
        <button onClick={() => void load()} style={{
          marginTop: 10, fontSize: 10, fontWeight: 700, fontFamily: T.font,
          color: T.white, background: T.teal, border: 'none',
          borderRadius: 8, padding: '7px 16px', cursor: 'pointer',
        }}>Retry</button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {showTabs && (
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto' }}>
          {tabs.map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              flexShrink: 0, padding: '5px 11px', borderRadius: 8,
              border: `1px solid ${activeTab === tab ? T.teal : T.border}`,
              background: activeTab === tab ? T.teal : T.white,
              color: activeTab === tab ? T.white : T.textSec,
              fontSize: 9, fontWeight: 600, fontFamily: T.font, cursor: 'pointer',
            }}>{tab}</button>
          ))}
        </div>
      )}

      {shown.length === 0 ? (
        <div style={{
          background: `${T.teal}08`, border: `1px solid ${T.border}`,
          borderRadius: 16, padding: 24, textAlign: 'center',
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: T.text, fontFamily: T.font, marginBottom: 4 }}>
            {orders.length === 0 ? 'No scan has been ordered for you' : `Nothing under ${activeTab}`}
          </div>
          <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, lineHeight: 1.5 }}>
            {orders.length === 0
              ? 'A study your doctor or hospital orders appears here, and its report once the centre releases it.'
              : 'Choose another filter to see the rest.'}
          </div>
        </div>
      ) : (
        shown.map(order => {
          const status = (order.status || '').toUpperCase();
          const d = details[order.id];
          const showing = openId === order.id;
          return (
            <div
              key={order.id}
              style={{
                background: T.white,
                border: `1px solid ${order.critical ? `${T.danger}55` : T.border}`,
                borderRadius: 14, padding: 14, boxShadow: T.shadow,
                display: 'flex', flexDirection: 'column', gap: 6,
              }}
            >
              {/* Header row: study + status */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <ScanIcon />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: T.text, fontFamily: T.font }}>
                    {titleOf(order)}
                  </div>
                  {subtitleOf(order) && (
                    <div style={{ fontSize: 9, color: T.textSec, fontFamily: T.font, marginTop: 1 }}>
                      {subtitleOf(order)}
                    </div>
                  )}
                  {/* Who ordered it, where, and when — one line, as the app has it. */}
                  <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 2, lineHeight: 1.5 }}>
                    {[
                      order.orderedBy ? `Ordered by ${order.orderedBy}` : null,
                      order.facility,
                      order.orderedAt ? fmtDate(order.orderedAt) : null,
                    ].filter(Boolean).join(' · ')}
                  </div>
                  {order.orderNumber && (
                    <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 1 }}>
                      {order.orderNumber}
                    </div>
                  )}
                </div>
                <span style={{
                  fontSize: 9, fontWeight: 700, fontFamily: T.font, color: T.white,
                  background: STATUS_COLORS[status] || T.textTer,
                  padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap', flexShrink: 0,
                }}>
                  {prettyStatus(status)}
                </span>
              </div>

              {/* The same chip row the app shows: only what is actually true. */}
              {(isUrgent(order) || order.scheduledFor || order.reportAvailable || order.critical) && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {isUrgent(order) && <Chip label={prettyStatus(order.priority || '')} tone="danger" />}
                  {order.scheduledFor && <Chip label={fmtDate(order.scheduledFor)} />}
                  {order.reportAvailable && <Chip label="Report ready" tone="success" />}
                  {order.critical && <Chip label="Critical finding" tone="danger" />}
                </div>
              )}

              {order.critical && (
                <div style={{
                  fontSize: 9, color: T.danger, fontFamily: T.font, fontWeight: 700,
                  background: `${T.danger}10`, border: `1px solid ${T.danger}30`,
                  borderRadius: 8, padding: '7px 10px', lineHeight: 1.5,
                }}>
                  Flagged critical by the centre — your doctor has been notified.
                </div>
              )}

              <button
                onClick={() => void toggle(order.id)}
                style={{
                  alignSelf: 'flex-start', padding: '6px 12px', fontSize: 9, fontWeight: 700,
                  fontFamily: T.font, color: T.teal, background: `${T.teal}10`,
                  border: `1px solid ${T.teal}30`, borderRadius: 6, cursor: 'pointer', marginTop: 4,
                }}
              >
                {showing ? 'Hide details' : 'Order details'}
              </button>

              {showing && detailError[order.id] && (
                <div style={{ fontSize: 9, color: T.danger, fontFamily: T.font, marginTop: 6 }}>
                  {detailError[order.id]}
                </div>
              )}

              {showing && d && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}`, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {d.report?.impression && (
                    <div style={{
                      fontSize: 10.5, color: T.text, fontFamily: T.font, lineHeight: 1.55,
                      background: `${T.success}0D`, border: `1px solid ${T.success}30`,
                      borderRadius: 8, padding: '9px 11px',
                    }}>
                      <span style={{ fontWeight: 800, color: T.success }}>Impression: </span>
                      {d.report.impression}
                      <div style={{ fontSize: 9, color: T.textTer, marginTop: 4 }}>
                        {[d.report.reportNumber, d.report.radiologistName, fmtDate(d.report.reportedAt)].filter(Boolean).join(' · ')}
                      </div>
                    </div>
                  )}

                  {/* The appointment the CENTRE booked — the app never picks one. */}
                  {slotLabel(d) && (
                    <div style={{ fontSize: 9.5, color: T.text, fontFamily: T.font }}>
                      <span style={{ fontWeight: 700 }}>Appointment: </span>{slotLabel(d)}
                      <span style={{ color: T.textTer }}> — booked by the imaging centre</span>
                    </div>
                  )}

                  {/* What a doctor asked for, in their own words. */}
                  {d.studies?.length > 0 && (
                    <div style={{ fontSize: 9.5, color: T.textSec, fontFamily: T.font, lineHeight: 1.5 }}>
                      <span style={{ fontWeight: 700, color: T.text }}>Requested: </span>
                      {d.studies.map(studyLabel).join(', ')}
                    </div>
                  )}

                  {d.clinicalNotes && (
                    <div style={{ fontSize: 9.5, color: T.textSec, fontFamily: T.font, lineHeight: 1.5 }}>
                      Why it was ordered: {d.clinicalNotes}
                    </div>
                  )}

                  {/* The centre's own word for where it is — shown, not rewritten. */}
                  {d.sourceStatus && d.sourceStatus.toUpperCase() !== (d.status || '').toUpperCase() && (
                    <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font }}>
                      Centre status: {prettyStatus(d.sourceStatus)}
                    </div>
                  )}

                  {/* Who captured the images, and how many — recorded by the centre. */}
                  {d.capture && (d.capture.technicianName || d.capture.imageCount != null || d.capture.qualityStatus) && (
                    <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, lineHeight: 1.5 }}>
                      {[
                        d.capture.technicianName ? `Imaged by ${d.capture.technicianName}` : null,
                        d.capture.imageCount != null ? `${d.capture.imageCount} image${d.capture.imageCount === 1 ? '' : 's'}` : null,
                        d.capture.qualityStatus ? prettyStatus(d.capture.qualityStatus) : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}

                  {[
                    d.contrastRequired ? 'Contrast required' : null,
                    d.radiationExposure ? `Radiation: ${prettyStatus(d.radiationExposure)}` : null,
                    d.safetyChecklist?.metalImplants ? 'Metal implants noted' : null,
                    d.pregnancyCaution ? `Pregnancy: ${d.pregnancyCaution}` : null,
                  ].filter(Boolean).length > 0 && (
                    <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, lineHeight: 1.5 }}>
                      {[
                        d.contrastRequired ? 'Contrast required' : null,
                        d.radiationExposure ? `Radiation: ${prettyStatus(d.radiationExposure)}` : null,
                        d.safetyChecklist?.metalImplants ? 'Metal implants noted' : null,
                        d.pregnancyCaution ? `Pregnancy: ${d.pregnancyCaution}` : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}

                  {d.timeline?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                      {d.timeline.map((ev) => (
                        <div key={`${ev.status}-${ev.at}`} style={{ fontSize: 9, color: T.textSec, fontFamily: T.font }}>
                          {ev.note || prettyStatus(ev.status)}
                          <span style={{ color: T.textTer }}> — {fmtDate(ev.at)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {order.reportAvailable ? (
                <button
                  onClick={() => navigate('/patient/health-vault')}
                  style={{
                    alignSelf: 'flex-end', padding: '6px 14px', fontSize: 9, fontWeight: 700,
                    fontFamily: T.font, color: T.white, background: T.teal,
                    border: 'none', borderRadius: 6, cursor: 'pointer', marginTop: 2,
                  }}
                >
                  View report
                </button>
              ) : (
                <div style={{ fontSize: 9, color: T.textTer, fontFamily: T.font, marginTop: 2 }}>
                  The report appears here once the centre releases it.
                </div>
              )}
            </div>
          );
        })
      )}

      {compact && filteredOrders.length > shown.length && (
        <button
          onClick={() => navigate('/patient/diagnostics')}
          style={{
            fontFamily: T.font, fontSize: 10, fontWeight: 700, color: T.teal,
            background: T.white, border: `1.5px solid ${T.teal}`, borderRadius: 10,
            padding: '9px 0', cursor: 'pointer',
          }}
        >
          See all {filteredOrders.length} scans
        </button>
      )}
    </div>
  );
}
